import type { Feature, Polygon } from 'geojson';
import type { RegionalOverviewV2Properties } from 'querys/regional-overview/regional-overview.query.models';
import { create } from 'zustand';

interface RegionalRiskOverviewState {
  textSearch: string;
  debouncedTextSearch: string;
  featuresWithCalculatedRisk: Feature<Polygon, RegionalOverviewV2Properties>[];
  filteredFeatures: Feature<Polygon, RegionalOverviewV2Properties>[];
  setTextSearch: (search: string) => void;
  setDebouncedTextSearch: (search: string) => void;
  setFeaturesWithCalculatedRisk: (features: Feature<Polygon, RegionalOverviewV2Properties>[]) => void;
  applyFilter: (searchText: string) => void;
  resetFilters: () => void;
  hasActiveFilter: () => boolean;
}

export const useRegionalRiskOverviewStore = create<RegionalRiskOverviewState>((set, get) => ({
  textSearch: '',
  debouncedTextSearch: '',
  featuresWithCalculatedRisk: [],
  filteredFeatures: [],

  setTextSearch: (search: string) => set({ textSearch: search }),

  setDebouncedTextSearch: (search: string) => {
    set({ debouncedTextSearch: search });
    get().applyFilter(search);
  },

  setFeaturesWithCalculatedRisk: (features: Feature<Polygon, RegionalOverviewV2Properties>[]) => {
    set({
      featuresWithCalculatedRisk: features,
      filteredFeatures: features
    });
    const currentSearch = get().debouncedTextSearch;
    if (currentSearch) {
      get().applyFilter(currentSearch);
    }
  },

  applyFilter: (searchText: string) => {
    const { featuresWithCalculatedRisk } = get();

    if (!searchText) {
      set({ filteredFeatures: featuresWithCalculatedRisk });
      return;
    }

    const searchLower = searchText.toLowerCase();

    const filtered = featuresWithCalculatedRisk.filter(feature => {
      const farmName = feature.properties.values?.property_name?.toLowerCase() ?? '';
      const fieldName = feature.properties.name?.toLowerCase() ?? '';
      const fieldId = feature.properties.id?.toLowerCase() ?? '';

      const searchableText = `${farmName} ${fieldName} ${fieldId}`;

      return searchableText.includes(searchLower);
    });

    set({ filteredFeatures: filtered });
  },

  resetFilters: () => {
    const { featuresWithCalculatedRisk } = get();
    set({
      textSearch: '',
      debouncedTextSearch: '',
      filteredFeatures: featuresWithCalculatedRisk
    });
  },

  hasActiveFilter: () => {
    return get().debouncedTextSearch !== '';
  }
}));
